import React, { useEffect, useState, useRef } from "react";
import {
  X,
  FileText,
  Loader2,
  DollarSign,
  Calendar,
  CreditCard,
  User,
  Info,
  Upload,
  CheckCircle2,
  Trash2,
  Building2,
  Plus,
  ListOrdered,
  Send,
} from "lucide-react";
import {
  createAdvanceRequest,
  updateAdvanceRequest,
  getAdvanceDetail,
  getAdvanceApproverCandidates,
  submitAdvanceRequest,
} from "../../../api/finance/advance/advanceRequests";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";

import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";

// ====== Convert number to Vietnamese text (basic) ======
const numberToVietnameseText = (number) => {
  if (!number || number === 0) return "";

  const units = ["", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
  const scales = ["", "nghìn", "triệu", "tỷ"];

  const readThreeDigits = (num) => {
    let hundred = Math.floor(num / 100);
    let ten = Math.floor((num % 100) / 10);
    let unit = num % 10;
    let result = "";

    if (hundred > 0) result += units[hundred] + " trăm ";

    if (ten > 1) {
      result += units[ten] + " mươi ";
      if (unit === 1) result += "mốt ";
      else if (unit === 5) result += "lăm ";
      else if (unit > 0) result += units[unit] + " ";
    } else if (ten === 1) {
      result += "mười ";
      if (unit === 5) result += "lăm ";
      else if (unit > 0) result += units[unit] + " ";
    } else if (unit > 0) {
      if (hundred > 0) result += "lẻ ";
      result += units[unit] + " ";
    }

    return result.trim();
  };

  let numStr = number.toString();
  let chunks = [];
  while (numStr.length > 0) {
    chunks.unshift(parseInt(numStr.slice(-3)));
    numStr = numStr.slice(0, -3);
  }

  let result = "";
  chunks.forEach((chunk, index) => {
    if (chunk > 0) {
      result +=
        readThreeDigits(chunk) +
        " " +
        scales[chunks.length - 1 - index] +
        " ";
    }
  });

  return result.trim() + " đồng";
};

// Hàm tính ngày làm việc sau một số ngày nhất định (bỏ qua thứ 7, chủ nhật)
const getBusinessDateAfter = (days) => {
  const date = new Date();
  let added = 0;

  while (added < days) {
    date.setDate(date.getDate() + 1);

    const day = date.getDay();
    // 0 = Sunday, 6 = Saturday

    if (day !== 0 && day !== 6) {
      added++;
    }
  }

  return date.toISOString().split("T")[0];
};



const VIETNAM_BANKS = [
  { value: "VCB", label: "Vietcombank - Ngân hàng Ngoại thương Việt Nam" },
  { value: "BIDV", label: "BIDV - Ngân hàng Đầu tư và Phát triển Việt Nam" },
  { value: "CTG", label: "VietinBank - Ngân hàng Công Thương Việt Nam" },
  { value: "TCB", label: "Techcombank - Ngân hàng Kỹ thương Việt Nam" },
  { value: "MB", label: "MBBank - Ngân hàng Quân đội" },
  { value: "VPB", label: "VPBank - Ngân hàng Thịnh vượng và Phát triển" },
  { value: "ACB", label: "ACB - Ngân hàng Á Châu" },
  { value: "TPB", label: "TPBank - Ngân hàng Tiên Phong" },
  { value: "HDB", label: "HDBank - Ngân hàng Phát triển TP.HCM" },
  { value: "VIB", label: "VIB - Ngân hàng Quốc tế" },
  { value: "SHB", label: "SHB - Ngân hàng Sài Gòn - Hà Nội" },
  { value: "STB", label: "Sacombank - Ngân hàng Sài Gòn Thương Tín" },
  { value: "MSB", label: "MSB - Ngân hàng Hàng Hải Việt Nam" },
  { value: "SEA", label: "SeABank - Ngân hàng Đông Nam Á" },
  { value: "OCB", label: "OCB - Ngân hàng Phương Đông" },
  { value: "LPB", label: "LPBank - Ngân hàng Lộc Phát Việt Nam" },
  { value: "ABB", label: "ABBANK - Ngân hàng An Bình" },
  { value: "VAB", label: "VietABank - Ngân hàng Việt Á" },
  { value: "NAB", label: "Nam A Bank - Ngân hàng Nam Á" },
  { value: "PGB", label: "PGBank - Ngân hàng Thịnh vượng và Phát triển" },
  { value: "EIB", label: "Eximbank - Ngân hàng Xuất Nhập khẩu Việt Nam" },
  { value: "VCCB", label: "BVBank - Ngân hàng Bản Việt" },
  { value: "PVB", label: "PVcomBank - Ngân hàng Đại chúng Việt Nam" },
  { value: "SCB", label: "SCB - Ngân hàng Sài Gòn" },
  { value: "KLB", label: "Kienlongbank - Ngân hàng Kiên Long" },
  { value: "BVB", label: "Baoviet Bank - Ngân hàng Bảo Việt" },
];


const ADVANCE_TYPES = [
  { value: "BUSINESS_TRIP", label: "Tạm ứng công tác" },
  { value: "PURCHASE", label: "Tạm ứng mua hàng" },
  { value: "PROJECT", label: "Tạm ứng dự án" },
  { value: "INTERNAL", label: "Tạm ứng nội bộ" },
];

const PAYMENT_METHODS = [
  { value: "CASH", label: "Tiền mặt" },
  { value: "BANK_TRANSFER", label: "Chuyển khoản" },
];

/** Từ mức này trở lên bắt buộc chuyển khoản */
const CASH_PAYMENT_MAX = 5_000_000;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("vi-VN");

/** Input đồng nhất trong nhóm Nhận tiền: cao 42px, text-sm, bo góc xl */
const RECEIVE_FIELD_CLASS =
  "w-full h-[42px] bg-white border border-slate-200 rounded-xl px-4 text-sm text-slate-800 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10 transition-colors";

const receiveMoneySelectStyles = {
  ...customSelectStyles,
  control: (base, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(base, state)
      : base),
    minHeight: "42px",
    height: "42px",
    borderRadius: "12px",
    fontSize: "0.875rem",
  }),
  valueContainer: (base) => ({
    ...(typeof customSelectStyles.valueContainer === "function"
      ? customSelectStyles.valueContainer(base)
      : base),
    height: "42px",
    padding: "0 12px",
    fontSize: "0.875rem",
  }),
  singleValue: (base) => ({
    ...(typeof customSelectStyles.singleValue === "function"
      ? customSelectStyles.singleValue(base)
      : base),
    fontSize: "0.875rem",
  }),
  placeholder: (base) => ({
    ...(typeof customSelectStyles.placeholder === "function"
      ? customSelectStyles.placeholder(base)
      : base),
    fontSize: "0.875rem",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: "0.875rem",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "42px",
  }),
  option: (base, state) => ({
    ...(typeof customSelectStyles.option === "function"
      ? customSelectStyles.option(base, state)
      : base),
    fontSize: "0.875rem",
  }),
  menu: (base) => ({
    ...(typeof customSelectStyles.menu === "function"
      ? customSelectStyles.menu(base)
      : base),
    borderRadius: "12px",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const newLine = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  description: "",
  amount: "",
});

const toDateInput = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const matchBankOption = (stored) => {
  if (!stored) return null;
  const found = VIETNAM_BANKS.find(
    (b) => b.label === stored || b.value === stored || b.label.includes(stored)
  );
  return found || { value: stored, label: stored };
};

const toApproverOptions = (candidates = []) =>
  candidates.map((c) => ({
    value: c.id,
    label: c.tag ? `${c.name} (${c.tag})` : c.name,
  }));

const normalizeAdvanceTypeValue = (stored) => {
  if (!stored) return "";
  const raw = String(stored).trim();
  const normalized = raw.toLowerCase();
  const found = ADVANCE_TYPES.find((type) => {
    const value = String(type.value).toLowerCase();
    const label = String(type.label).toLowerCase();
    return value === normalized || label === normalized || label.includes(normalized);
  });
  return found?.value || raw;
};

const AdvanceCreateModal = ({ isOpen, onClose, onSaved, editId = null, copyFromId = null }) => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const firstInputRef = useRef(null);
  const isEdit = Boolean(editId);
  const isCopy = Boolean(copyFromId) && !isEdit;
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [advanceType, setAdvanceType] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [requestDate, setRequestDate] = useState("");
  const [repaymentDueDate, setRepaymentDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("BANK_TRANSFER");
  const [attachedFile, setAttachedFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [dragOverAttach, setDragOverAttach] = useState(false);
  const [bankName, setBankName] = useState(null);
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [shouldReset, setShouldReset] = useState(true);
  const [editCode, setEditCode] = useState("");
  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedFirstApprover, setSelectedFirstApprover] = useState(null);
  const [approversLoading, setApproversLoading] = useState(false);

  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const applyDetailToForm = (detail, { asCopy }) => {
      if (!asCopy) setEditCode(detail.request_code || "");
      else setEditCode("");
      setAdvanceType(normalizeAdvanceTypeValue(detail.advance_type));
      if (asCopy) {
        setRequestDate(getBusinessDateAfter(3));
      } else {
        setRequestDate(toDateInput(detail.request_date));
      }
      setRepaymentDueDate(toDateInput(detail.repayment_due_date));
      setPaymentMethod(
        String(detail.payment_method || "BANK_TRANSFER").toUpperCase()
      );
      setBankName(matchBankOption(detail.bank_name));
      setBankAccountNumber(detail.account_number || "");
      setAttachedFile(null);
      if (!asCopy && detail.pdf_url) {
        const nameFromUrl = String(detail.pdf_url).split("/").pop() || "Đã đính kèm PDF";
        try {
          setPreviewName(decodeURIComponent(nameFromUrl));
        } catch {
          setPreviewName(nameFromUrl);
        }
      } else {
        setPreviewName("");
      }
      const detailLines = Array.isArray(detail.lines) ? detail.lines : [];
      setLines(
        detailLines.length
          ? detailLines.map((line) => ({
              id: newLine().id,
              description: line.description || "",
              amount: String(line.amount ?? ""),
            }))
          : [
              {
                id: newLine().id,
                description: detail.advance_reason || "",
                amount: String(detail.advance_amount || ""),
              },
            ]
      );
      setShouldReset(false);
      setTimeout(() => firstInputRef.current?.focus(), 200);
    };

    if (editId || copyFromId) {
      let cancelled = false;
      const sourceId = editId || copyFromId;
      const load = async () => {
        try {
          setLoadingEdit(true);
          const detail = await getAdvanceDetail(sourceId);
          if (cancelled) return;
          if (editId && detail?.status !== "draft") {
            showErrorToast("Chỉ sửa được phiếu ở trạng thái Nháp.");
            onClose?.();
            return;
          }
          applyDetailToForm(detail, { asCopy: Boolean(copyFromId) && !editId });
        } catch (err) {
          console.error(err);
          showErrorToast(
            err?.response?.data?.message ||
              (editId
                ? "Không tải được phiếu để sửa."
                : "Không tải được phiếu để sao chép.")
          );
          onClose?.();
        } finally {
          if (!cancelled) setLoadingEdit(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }

    if (shouldReset) {
      const defaultDate = getBusinessDateAfter(3);
      setRequestDate(defaultDate);
      setRepaymentDueDate("");
      setAdvanceType("");
      setLines([newLine()]);
      setPaymentMethod("BANK_TRANSFER");
      setAttachedFile(null);
      setPreviewName("");
      setDragOverAttach(false);
      setBankName(null);
      setBankAccountNumber("");
      setEditCode("");
      setSelectedFirstApprover(null);
      setShouldReset(false);
      setTimeout(() => firstInputRef.current?.focus(), 200);
    }
  }, [isOpen, editId, copyFromId, shouldReset, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const requesterId = user?.id || user?.userId;
    if (!requesterId) return undefined;

    let cancelled = false;
    const loadApprovers = async () => {
      try {
        setApproversLoading(true);
        const meta = await getAdvanceApproverCandidates({
          purpose: "first",
          requester_id: requesterId,
        });
        if (cancelled) return;
        const opts = toApproverOptions(meta?.candidates || []);
        setApproverOptions(opts);
        setSelectedFirstApprover((prev) => {
          if (prev && opts.some((o) => o.value === prev.value)) return prev;
          return opts.length === 1 ? opts[0] : null;
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setApproverOptions([]);
          setSelectedFirstApprover(null);
        }
      } finally {
        if (!cancelled) setApproversLoading(false);
      }
    };
    loadApprovers();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.id, user?.userId]);

  const handleCancel = async () => {
    if (loading) return;

    const confirm = await showConfirmDialog({
      title: isEdit ? "Hủy chỉnh sửa" : "Xác nhận hủy",
      text: isEdit
        ? "Bạn có muốn đóng form và bỏ các thay đổi chưa lưu?"
        : "Bạn có muốn xóa toàn bộ dữ liệu đang nhập?",
      confirmText: isEdit ? "Đóng" : "Xóa",
      cancelText: "Giữ lại",
    });

    if (confirm.isConfirmed) {
      setShouldReset(true);
      onClose();
    }
  };

  const handleCloseInstant = () => {
    setShouldReset(false);
    onClose();
  };

  const applyPdfFile = (file) => {
    if (!file) return;
    const isPdf =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) {
      showErrorToast("Chỉ cho phép file PDF.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorToast("File vượt quá 25MB.");
      return;
    }
    setAttachedFile(file);
    setPreviewName(file.name);
    setDragOverAttach(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    applyPdfFile(file);
  };

  const handleAttachDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setDragOverAttach(true);
  };

  const handleAttachDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverAttach(false);
  };

  const handleAttachDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverAttach(false);
    applyPdfFile(e.dataTransfer?.files?.[0]);
  };

  const updateLine = (id, patch) => {
    setLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, ...patch } : line))
    );
  };

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  const removeLine = (id) => {
    setLines((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((line) => line.id !== id);
    });
  };

  const buildValidatedFormData = () => {
    if (!advanceType || !paymentMethod) {
      showErrorToast("Vui lòng nhập đầy đủ thông tin bắt buộc.");
      return null;
    }

    if (!repaymentDueDate) {
      showErrorToast("Vui lòng chọn ngày hoàn tạm ứng.");
      return null;
    }

    if (requestDate && repaymentDueDate < requestDate) {
      showErrorToast("Ngày hoàn tạm ứng phải từ ngày nhận tiền trở đi.");
      return null;
    }

    const normalizedLines = [];
    for (let i = 0; i < lines.length; i += 1) {
      const description = String(lines[i].description || "").trim();
      const amount = Number(String(lines[i].amount || "").replace(/,/g, ""));
      if (!description) {
        showErrorToast(`Dòng ${i + 1}: vui lòng nhập lý do tạm ứng.`);
        return null;
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        showErrorToast(`Dòng ${i + 1}: số tiền phải lớn hơn 0.`);
        return null;
      }
      normalizedLines.push({ description, amount });
    }

    if (paymentMethod === "BANK_TRANSFER") {
      if (!bankName || !bankAccountNumber) {
        showErrorToast("Vui lòng nhập đầy đủ thông tin ngân hàng.");
        return null;
      }
    }

    const total = normalizedLines.reduce((sum, line) => sum + line.amount, 0);
    if (total >= CASH_PAYMENT_MAX && paymentMethod === "CASH") {
      showErrorToast(
        "Từ 5.000.000₫ trở lên bắt buộc nhận tiền bằng chuyển khoản."
      );
      return null;
    }

    const formData = new FormData();
    formData.append("employee_id", user?.id || "");
    formData.append("department_id", user?.department_id || "");
    formData.append("advance_type", advanceType);
    formData.append("request_date", requestDate);
    formData.append("repayment_due_date", repaymentDueDate);
    formData.append("payment_method", paymentMethod);
    formData.append("lines", JSON.stringify(normalizedLines));

    if (paymentMethod === "BANK_TRANSFER") {
      formData.append("bank_name", bankName?.label || bankName?.value || "");
      formData.append("account_number", bankAccountNumber);
      formData.append(
        "account_holder",
        user?.name || user?.employee_name || ""
      );
    }

    if (attachedFile) {
      formData.append("pdf_file", attachedFile);
    }

    return formData;
  };

  /** @param {'draft'|'submit'} mode */
  const handleSave = async (mode = "draft") => {
    const formData = buildValidatedFormData();
    if (!formData) return;

    if (mode === "submit" && !selectedFirstApprover?.value) {
      showErrorToast("Vui lòng chọn người duyệt cấp 1 trước khi gửi duyệt.");
      return;
    }

    if (mode === "submit") {
      const confirm = await showConfirmDialog({
        title: "Gửi duyệt đề nghị",
        text: `Lưu và gửi đến ${selectedFirstApprover.label}? Phiếu sẽ chuyển sang Chờ duyệt.`,
        confirmText: "Gửi duyệt",
        cancelText: "Để sau",
      });
      if (!confirm?.isConfirmed) return;
    }

    try {
      setLoading(true);

      let advanceId = editId;
      if (isEdit) {
        await updateAdvanceRequest(editId, formData);
      } else {
        const created = await createAdvanceRequest(formData);
        advanceId = created?.id;
        if (!advanceId) {
          throw new Error("Không nhận được mã phiếu sau khi tạo.");
        }
      }

      if (mode === "submit") {
        await submitAdvanceRequest(advanceId, {
          first_approver_id: selectedFirstApprover.value,
        });
        showSuccessToast("Đã gửi đề nghị tạm ứng đi duyệt.");
      } else if (isEdit) {
        showSuccessToast("Đã cập nhật đề nghị tạm ứng.");
      } else {
        showSuccessToast("Đã lưu đề nghị tạm ứng ở trạng thái Nháp.");
      }

      setShouldReset(true);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.response?.data?.message ||
          (mode === "submit"
            ? "Không thể gửi duyệt đề nghị tạm ứng."
            : isEdit
              ? "Không thể cập nhật đề nghị tạm ứng."
              : "Không thể tạo đề nghị tạm ứng.")
      );
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = lines.reduce(
    (sum, line) => sum + (Number(String(line.amount || "").replace(/,/g, "")) || 0),
    0
  );
  const requireBankTransfer = totalAmount >= CASH_PAYMENT_MAX;
  const availablePaymentMethods = requireBankTransfer
    ? PAYMENT_METHODS.filter((m) => m.value === "BANK_TRANSFER")
    : PAYMENT_METHODS;

  useEffect(() => {
    if (!requireBankTransfer) return;
    if (paymentMethod !== "BANK_TRANSFER") {
      setPaymentMethod("BANK_TRANSFER");
    }
  }, [requireBankTransfer, paymentMethod]);

  const filledLines = lines.filter(
    (line) =>
      String(line.description || "").trim() &&
      Number(String(line.amount || "").replace(/,/g, "")) > 0
  ).length;
  const selectedAdvanceType =
    ADVANCE_TYPES.find((t) => t.value === advanceType) ||
    (advanceType ? { value: advanceType, label: advanceType } : null);
  const selectedPaymentMethod =
    availablePaymentMethods.find((m) => m.value === paymentMethod) ||
    PAYMENT_METHODS.find((m) => m.value === paymentMethod) ||
    null;
  const completionCount = [
    advanceType,
    filledLines > 0,
    paymentMethod,
    requestDate,
    repaymentDueDate,
  ].filter(Boolean).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[50]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseInstant}
          />

          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[51] p-4"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-6xl flex flex-col max-h-[92vh] overflow-hidden border border-slate-100">
              {isMobileViewport ? (
                <div className="p-6 flex flex-col items-center justify-center text-center min-h-[280px]">
                  <div className="p-3 rounded-2xl bg-red-50 text-red-600 mb-4">
                    <FileText size={28} />
                  </div>
                  <h2 className="text-lg font-black text-slate-800">
                    {isEdit ? "Sửa đề nghị trên máy tính" : isCopy ? "Sao chép đề nghị trên máy tính" : "Tạo đề nghị trên máy tính"}
                  </h2>
                  <p className="text-sm text-slate-500 mt-2 max-w-sm">
                    Form đề nghị tạm ứng nhiều dòng được tối ưu cho desktop.
                    Trên điện thoại bạn vẫn có thể xem và phê duyệt.
                  </p>
                  <button
                    type="button"
                    onClick={handleCloseInstant}
                    className="mt-6 px-6 py-3 rounded-2xl bg-red-700 text-white text-xs font-black uppercase tracking-wider"
                  >
                    Đóng
                  </button>
                </div>
              ) : loadingEdit ? (
                <div className="p-12 flex flex-col items-center justify-center min-h-[280px] text-slate-500 gap-3">
                  <Loader2 size={28} className="animate-spin text-red-600" />
                  <p className="text-sm font-medium">
                    {isCopy ? "Đang tải phiếu để sao chép..." : "Đang tải phiếu nháp..."}
                  </p>
                </div>
              ) : (
                <>
              <div className="px-6 md:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="p-2.5 bg-red-600 rounded-2xl text-white shadow-lg shadow-red-200">
                        <FileText size={20} />
                      </span>
                      <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight">
                          {isEdit
                            ? "Sửa đề nghị tạm ứng"
                            : isCopy
                              ? "Sao chép đề nghị tạm ứng"
                              : "Tạo đề nghị tạm ứng"}
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                          {isEdit
                            ? `Cập nhật phiếu nháp${editCode ? ` ${editCode}` : ""} rồi lưu lại.`
                            : isCopy
                              ? "Dữ liệu được sao chép sang phiếu mới. File đính kèm cần chọn lại nếu cần."
                              : "Nhập thông tin trên một màn hình để theo dõi tổng thể và gửi đề nghị nhanh hơn."}
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseInstant}
                    className="text-slate-400 hover:text-red-600 p-2 rounded-xl hover:bg-red-50 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <StatusChip
                    icon={User}
                    label={user?.name || user?.employee_name || "Nhân viên"}
                  />
                  <StatusChip
                    icon={Building2}
                    label={user?.department_name || "Chưa có phòng ban"}
                  />
                  <StatusChip
                    icon={CheckCircle2}
                    label={`Hoàn thiện ${completionCount}/5 mục chính`}
                    highlighted
                  />
                </div>
              </div>

              <div className="px-6 md:px-8 py-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_360px] gap-6 items-start">
                  <div className="space-y-6">
                    <SectionCard
                      icon={FileText}
                      title="Thông tin đề nghị"
                      description="Chọn loại tạm ứng và khai báo các dòng tạm ứng theo từng lý do."
                    >
                      <div>
                        <FieldLabel label="Loại tạm ứng" required />
                        <Select
                          ref={firstInputRef}
                          value={selectedAdvanceType}
                          onChange={(selected) =>
                            setAdvanceType(selected?.value || "")
                          }
                          options={ADVANCE_TYPES}
                          styles={customSelectStyles}
                          placeholder="Chọn mục đích tạm ứng..."
                        />
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={ListOrdered}
                      title="Chi tiết tạm ứng"
                      description="Mỗi dòng là một lý do và số tiền cần tạm ứng."
                    >
                      <div className="overflow-x-auto rounded-2xl border border-slate-200">
                        <table className="w-full min-w-[640px] border-collapse">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-3 py-3 text-center text-[11px] font-black uppercase tracking-wider text-slate-500 w-14">
                                STT
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-black uppercase tracking-wider text-slate-500">
                                Lý do tạm ứng *
                              </th>
                              <th className="px-3 py-3 text-right text-[11px] font-black uppercase tracking-wider text-slate-500 w-48">
                                Số tiền *
                              </th>
                              <th className="px-3 py-3 text-center text-[11px] font-black uppercase tracking-wider text-slate-500 w-16">
                                Xóa
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((line, index) => (
                              <tr
                                key={line.id}
                                className="border-t border-slate-100"
                              >
                                <td className="px-3 py-3 text-center text-sm font-semibold text-slate-500">
                                  {index + 1}
                                </td>
                                <td className="px-3 py-3">
                                  <input
                                    type="text"
                                    value={line.description}
                                    onChange={(e) =>
                                      updateLine(line.id, {
                                        description: e.target.value,
                                      })
                                    }
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                                    placeholder="Nhập lý do tạm ứng..."
                                  />
                                </td>
                                <td className="px-3 py-3">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                      line.amount
                                        ? Number(
                                            String(line.amount).replace(/,/g, "")
                                          ).toLocaleString("vi-VN")
                                        : ""
                                    }
                                    onChange={(e) =>
                                      updateLine(line.id, {
                                        amount: e.target.value
                                          .replace(/,/g, "")
                                          .replace(/[^\d]/g, ""),
                                      })
                                    }
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-right font-semibold text-red-700 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-3 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removeLine(line.id)}
                                    disabled={lines.length <= 1}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                                    title="Xóa dòng"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <button
                          type="button"
                          onClick={addLine}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-red-700 hover:bg-red-100"
                        >
                          <Plus size={16} />
                          Thêm dòng
                        </button>
                        <div className="text-right">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                            Tổng tiền ({lines.length} dòng)
                          </p>
                          <p className="text-xl font-black text-red-700">
                            {formatCurrency(totalAmount)} ₫
                          </p>
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={CreditCard}
                      title="Nhận tiền"
                      description="Thiết lập ngày cần nhận tiền và hình thức thanh toán phù hợp."
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div>
                          <FieldLabel label="Ngày nhận tiền" />
                          <div className="relative">
                            <Calendar
                              size={16}
                              className="pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-slate-400"
                            />
                            <input
                              type="date"
                              value={requestDate}
                              onChange={(e) => {
                                const next = e.target.value;
                                setRequestDate(next);
                                if (repaymentDueDate && next && repaymentDueDate < next) {
                                  setRepaymentDueDate("");
                                }
                              }}
                              className={`${RECEIVE_FIELD_CLASS} pl-10`}
                            />
                          </div>
                        </div>

                        <div>
                          <FieldLabel label="Ngày hoàn tạm ứng" required />
                          <div className="relative">
                            <Calendar
                              size={16}
                              className="pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-slate-400"
                            />
                            <input
                              type="date"
                              value={repaymentDueDate}
                              min={requestDate || undefined}
                              onChange={(e) => setRepaymentDueDate(e.target.value)}
                              className={`${RECEIVE_FIELD_CLASS} pl-10`}
                            />
                          </div>
                        </div>

                        <div>
                          <FieldLabel label="Hình thức thanh toán" required />
                          <Select
                            value={selectedPaymentMethod}
                            onChange={(selected) =>
                              setPaymentMethod(selected?.value || "")
                            }
                            options={availablePaymentMethods}
                            isOptionDisabled={(opt) =>
                              requireBankTransfer && opt.value === "CASH"
                            }
                            styles={receiveMoneySelectStyles}
                            placeholder="Chọn hình thức nhận tiền..."
                          />
                          <p className="mt-2 text-xs text-slate-500">
                            {requireBankTransfer
                              ? "Từ 5.000.000₫ trở lên bắt buộc chuyển khoản."
                              : "Dưới 5.000.000₫ có thể chọn Tiền mặt hoặc Chuyển khoản."}
                          </p>
                        </div>
                      </div>

                      {paymentMethod === "BANK_TRANSFER" ? (
                        <div className="mt-5 p-5 bg-slate-50 rounded-xl border border-slate-100">
                          <div className="flex items-center gap-2 mb-4">
                            <Info size={16} className="text-blue-500" />
                            <p className="text-sm font-semibold text-slate-700">
                              Thông tin chuyển khoản
                            </p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                              <FieldLabel label="Ngân hàng" required />
                              <Select
                                value={bankName}
                                onChange={(selected) => setBankName(selected)}
                                options={VIETNAM_BANKS}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={receiveMoneySelectStyles}
                                placeholder="Chọn ngân hàng..."
                                isSearchable
                              />
                            </div>
                            <div>
                              <FieldLabel label="Số tài khoản" required />
                              <input
                                type="text"
                                value={bankAccountNumber}
                                onChange={(e) =>
                                  setBankAccountNumber(
                                    e.target.value.replace(/\D/g, "")
                                  )
                                }
                                className={`${RECEIVE_FIELD_CLASS} font-mono tabular-nums`}
                                placeholder="Nhập số tài khoản..."
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </SectionCard>
                  </div>

                  <div className="space-y-5 xl:sticky xl:top-0">
                    <SectionCard icon={Upload} title="Tài liệu đính kèm">
                      {attachedFile || previewName ? (
                        <div
                          className={`rounded-xl border bg-slate-50 p-4 transition-colors ${
                            dragOverAttach
                              ? "border-red-400 border-dashed ring-2 ring-red-200 bg-red-50/60"
                              : "border-slate-200"
                          }`}
                          onDragEnter={handleAttachDragOver}
                          onDragOver={handleAttachDragOver}
                          onDragLeave={handleAttachDragLeave}
                          onDrop={handleAttachDrop}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-red-600 shrink-0">
                              <FileText size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-700 truncate">
                                {previewName || "file.pdf"}
                              </p>
                              {attachedFile ? (
                                <p className="text-xs text-slate-400 mt-1">
                                  {(attachedFile.size / (1024 * 1024)).toFixed(2)}{" "}
                                  MB
                                </p>
                              ) : (
                                <p className="text-xs text-slate-400 mt-1">
                                  File đã lưu trên phiếu · kéo thả PDF để thay thế
                                </p>
                              )}
                              {dragOverAttach ? (
                                <p className="mt-2 text-xs font-semibold text-red-600">
                                  Thả PDF để thay thế…
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setAttachedFile(null);
                                setPreviewName("");
                                setDragOverAttach(false);
                              }}
                              className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                              title="Xóa file"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                          <label
                            htmlFor="advanceFileInput"
                            className="mt-3 inline-flex cursor-pointer text-xs font-semibold text-red-600 hover:underline"
                          >
                            Đổi file khác
                            <input
                              type="file"
                              id="advanceFileInput"
                              className="hidden"
                              accept=".pdf,application/pdf"
                              onChange={handleFileSelect}
                            />
                          </label>
                        </div>
                      ) : (
                        <label
                          htmlFor="advanceFileInput"
                          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors text-center ${
                            dragOverAttach
                              ? "border-red-500 bg-red-50 ring-2 ring-red-200"
                              : "border-slate-200 hover:bg-red-50/60 hover:border-red-200"
                          }`}
                          onDragEnter={handleAttachDragOver}
                          onDragOver={handleAttachDragOver}
                          onDragLeave={handleAttachDragLeave}
                          onDrop={handleAttachDrop}
                        >
                          <Upload
                            className={`mb-3 ${
                              dragOverAttach ? "text-red-600" : "text-slate-400"
                            }`}
                            size={22}
                          />
                          <p className="text-sm font-semibold text-slate-700">
                            {dragOverAttach
                              ? "Thả file PDF vào đây"
                              : "Kéo thả hoặc chọn file PDF"}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Dung lượng tối đa 25MB
                          </p>
                          <input
                            type="file"
                            id="advanceFileInput"
                            className="hidden"
                            accept=".pdf,application/pdf"
                            onChange={handleFileSelect}
                          />
                        </label>
                      )}
                    </SectionCard>

                    <div className="rounded-[1.75rem] border border-slate-200 bg-slate-900 text-white p-5 shadow-xl shadow-slate-900/10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-11 w-11 rounded-2xl bg-white/10 flex items-center justify-center">
                          <DollarSign size={20} />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">
                            Tóm tắt đề nghị
                          </p>
                          <p className="font-bold text-base">Tổng quan phiếu</p>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white/10 border border-white/10 px-4 py-4 mb-4">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-white/50">
                          Tổng tiền đề nghị
                        </p>
                        <p className="text-2xl font-black text-white mt-1 tabular-nums">
                          {totalAmount > 0
                            ? `${formatCurrency(totalAmount)} ₫`
                            : "0 ₫"}
                        </p>
                        <p className="text-xs text-white/55 mt-2 italic">
                          {totalAmount > 0
                            ? numberToVietnameseText(totalAmount)
                            : "Thêm dòng tạm ứng để hiển thị tổng tiền"}
                        </p>
                      </div>

                      <div className="space-y-2.5 text-sm">
                        <SummaryRow
                          label="Số dòng"
                          value={`${filledLines}/${lines.length} dòng hợp lệ`}
                          dark
                        />
                        <SummaryRow
                          label="Loại tạm ứng"
                          value={
                            selectedAdvanceType?.label || "Chưa chọn loại tạm ứng"
                          }
                          dark
                        />
                        <SummaryRow
                          label="Tài khoản nhận"
                          value={
                            paymentMethod === "BANK_TRANSFER"
                              ? bankAccountNumber || "Chưa nhập số tài khoản"
                              : "Không yêu cầu"
                          }
                          dark
                        />
                        <SummaryRow
                          label="Đính kèm"
                          value={previewName || "Chưa có tài liệu"}
                          dark
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <Send size={16} className="text-red-600" />
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                          Người duyệt cấp 1
                        </p>
                      </div>
                      <Select
                        value={selectedFirstApprover}
                        onChange={setSelectedFirstApprover}
                        options={approverOptions}
                        isLoading={approversLoading}
                        styles={receiveMoneySelectStyles}
                        placeholder={
                          approversLoading
                            ? "Đang tải..."
                            : "Chọn người duyệt..."
                        }
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        noOptionsMessage={() => "Không có ứng viên duyệt"}
                      />
                      <p className="mt-2 text-xs text-slate-400">
                        Bắt buộc khi bấm Gửi duyệt. Lưu nháp chưa cần chọn.
                      </p>
                    </div>

                    <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center">
                          <User size={20} />
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">
                            Người đề nghị
                          </p>
                          <p className="font-bold text-base text-slate-800">
                            {user?.name || user?.employee_name || "Chưa xác định"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2.5 text-sm">
                        <SummaryRow
                          label="Phòng ban"
                          value={user?.department_name || "Chưa có dữ liệu"}
                        />
                        <SummaryRow
                          label="Ngày cần nhận"
                          value={requestDate || "Chưa chọn"}
                        />
                        <SummaryRow
                          label="Ngày hoàn tạm ứng"
                          value={repaymentDueDate || "Chưa chọn"}
                        />
                        <SummaryRow
                          label="Hình thức"
                          value={
                            selectedPaymentMethod?.label || "Chưa chọn hình thức"
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 md:px-8 py-5 border-t border-slate-100 bg-slate-50/80 flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 font-bold text-slate-500 hover:text-red-600 transition-colors rounded-2xl"
                >
                  Hủy bỏ
                </button>

                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleSave("draft")}
                    disabled={loading || loadingEdit}
                    className="px-6 py-3 bg-white border border-slate-200 hover:border-red-200 hover:bg-red-50 text-slate-700 hover:text-red-700 rounded-2xl font-bold disabled:opacity-70 flex items-center justify-center gap-2 transition-all active:scale-95"
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={18} />
                    )}
                    {isEdit ? "Lưu thay đổi" : "Lưu nháp"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave("submit")}
                    disabled={loading || loadingEdit || approversLoading}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold shadow-lg shadow-red-200 disabled:opacity-70 flex items-center justify-center gap-2 transition-all active:scale-95"
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                    Gửi duyệt
                  </button>
                </div>
              </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AdvanceCreateModal;

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-100 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          {description ? (
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ label, required = false }) {
  return (
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
  );
}

function SummaryRow({ label, value, dark = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={dark ? "text-white/60 text-xs" : "text-slate-400 text-xs"}>
        {label}
      </span>
      <span
        className={
          dark
            ? "text-right text-sm font-semibold text-white"
            : "text-right text-sm font-semibold text-slate-700"
        }
      >
        {value}
      </span>
    </div>
  );
}

function StatusChip({ icon: Icon, label, highlighted = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold border ${
        highlighted
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-white text-slate-600 border-slate-200"
      }`}
    >
      <Icon size={14} />
      {label}
    </span>
  );
}